import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { jailFine } from '@tigrinho/engine';
import { clock } from '../lib/format';
import { ROLL_MS, useNow } from '../net/useRoom';
import { prefs } from '../lib/storage';
import { useRoomCtx } from '../screens/RoomScreen';
import type { Layout } from './geometry';
import { type Decision, decider, decision } from './selectors';

const PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

/** Rotação do cubo para mostrar cada face de frente. */
const FACE_TURN: Record<number, [number, number]> = { 1: [0, 0], 2: [90, 0], 3: [0, -90], 4: [0, 90], 5: [-90, 0], 6: [0, 180] };

/** Dado 3D (cubo de verdade). Rola girando e assenta no número sorteado. */
function Die({ value, rolling, spin }: { value: number; rolling: boolean; spin: number }) {
  const [x, y] = FACE_TURN[value] ?? [0, 0];
  // Voltas extras para o dado "assentar" girando depois da rolagem.
  const turn = `rotateX(${x + spin * 360}deg) rotateY(${y + spin * 360}deg)`;
  return (
    <span className="die-throw">
    <span className="die3d" role="img" aria-label={rolling ? 'dado rolando' : `dado ${value}`}>
      <span className={`cube ${rolling ? 'is-rolling' : ''}`} style={rolling ? undefined : { transform: turn }}>
        {[1, 2, 3, 4, 5, 6].map((f) => (
          <span key={f} className={`dface f${f}`}>
            {Array.from({ length: 9 }, (_, i) => (
              <i key={i} className={PIPS[f]?.includes(i) ? 'on' : ''} />
            ))}
          </span>
        ))}
      </span>
    </span>
    </span>
  );
}

/**
 * Arremesso: os dados saem do placar de quem jogou, voam num arco, quicam na mesa e
 * rolam até parar (sem mão aparecendo). Dura o mesmo tempo que a rolagem no servidor.
 */
function throwDice(box: HTMLElement, fromId: string): void {
  const chip = document.getElementById(fromId)?.getBoundingClientRect();
  const dice = [...box.querySelectorAll<HTMLElement>('.die-throw')];
  dice.forEach((el, i) => {
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    // Sem placar na tela (ou muito perto): vem de baixo.
    let dx = chip ? chip.left + chip.width / 2 - cx : 0;
    let dy = chip ? chip.top + chip.height / 2 - cy : window.innerHeight * 0.5;
    if (Math.hypot(dx, dy) < 80) dy = window.innerHeight * 0.5;
    const side = i === 0 ? -1 : 1;
    const lift = -Math.max(70, Math.min(160, Math.abs(dy) * 0.35));
    el.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) scale(.45) rotate(${side * -40}deg)`, opacity: 0, offset: 0 },
        { transform: `translate(${dx * 0.92}px, ${dy * 0.92}px) scale(.6) rotate(${side * -30}deg)`, opacity: 1, offset: 0.06 },
        { transform: `translate(${dx * 0.4}px, ${dy * 0.4 + lift}px) scale(1.18) rotate(${side * 60}deg)`, offset: 0.3, easing: 'ease-in' },
        { transform: `translate(${side * 6}px, 0) scale(1) rotate(${side * 120}deg)`, offset: 0.48, easing: 'ease-out' },
        { transform: `translate(${-dx * 0.05 + side * 10}px, ${-26}px) scale(1.05) rotate(${side * 170}deg)`, offset: 0.6, easing: 'ease-in' },
        { transform: `translate(${side * 4}px, 0) scale(1) rotate(${side * 220}deg)`, offset: 0.72, easing: 'ease-out' },
        { transform: `translate(${side * 2}px, -8px) scale(1.01) rotate(${side * 240}deg)`, offset: 0.8, easing: 'ease-in' },
        { transform: `translate(0, 0) scale(1) rotate(${side * 260}deg)`, offset: 0.88 },
        { transform: `translate(0, 0) scale(1) rotate(${side * 360}deg)`, offset: 1 },
      ],
      { duration: ROLL_MS, easing: 'linear', fill: 'none' },
    );
  });
}

/** Contagem regressiva da decisão atual. */
export function useCountdown(): { left: number; total: number } | null {
  const { room, serverNow } = useRoomCtx();
  useNow(200);
  const t = room.timer;
  if (!t) return null;
  return { left: Math.max(0, t.deadline - serverNow()), total: t.seconds * 1000 };
}

/** Texto do balão em cima do peão de quem está decidindo (como o "Rolling dice..." do BT). */
export function bubbleText(d: Decision): string | null {
  switch (d.kind) {
    case 'turn':
      return d.phase === 'roll' ? 'Jogando os dados…' : null;
    case 'debt':
      return 'Fazendo as contas…';
    case 'prompt':
      switch (d.prompt.kind) {
        case 'build':
          return d.prompt.from < 0 ? 'Comprando…' : 'Construindo…';
        case 'takeover':
          return 'Pensando em tomar…';
        case 'championship':
          return 'Escolhendo a Copa…';
        case 'tour':
          return 'Escolhendo o destino…';
        case 'card':
          return 'Lendo a carta…';
      }
  }
  return null;
}

/** Centro do tabuleiro: relógio, dados e o botão da vez. */
export function Hud({ layout, news, onShop }: { layout: Layout; news?: { text: string; tone: string; id: number } | null; onShop: () => void }) {
  const ctx = useRoomCtx();
  const { room, you, act, name, money, serverNow } = ctx;
  const g = room.game!;
  // Ao tocar em "Jogar dados" o dado já começa a girar (antes da resposta do servidor chegar).
  const [pressed, setPressed] = useState(false);
  useEffect(() => {
    if (ctx.rolling) setPressed(false);
  }, [ctx.rolling]);
  useEffect(() => {
    if (!pressed) return;
    const t = setTimeout(() => setPressed(false), 2500);
    return () => clearTimeout(t);
  }, [pressed]);
  const rolling = !!ctx.rolling || pressed;
  const [spin, setSpin] = useState(0);
  const d = decision(g);
  const who = decider(d);
  const cd = useCountdown();
  const secs = cd ? Math.ceil(cd.left / 1000) : null;
  const me = g.players.find((p) => p.id === you);

  // Cada rolagem termina com voltas a mais, para o dado assentar girando.
  useEffect(() => ctx.bus.onRoll(() => setSpin((v) => v + 1)), [ctx.bus]);

  // Começou a rolar: arremessa os dados a partir do placar de quem jogou.
  const diceBox = useRef<HTMLDivElement>(null);
  const thrower = ctx.rolling?.player ?? (pressed ? you : null);
  const wasRolling = useRef(false);
  useLayoutEffect(() => {
    if (rolling && !wasRolling.current && diceBox.current && thrower && !prefs.reducedEffects()) throwDice(diceBox.current, `chip-${thrower}`);
    wasRolling.current = rolling;
  }, [rolling, thrower]);

  const c = room.clock;
  const elapsed = c ? (c.paused ? c.elapsedMs : c.elapsedMs + (serverNow() - room.serverNow)) : 0;
  const left = c ? c.durationMs - elapsed : 0;
  const roll = g.lastRoll;
  const mine = who === you && !!me;
  const myRoll = mine && d.kind === 'turn' && d.phase === 'roll' && !rolling;
  const compact = layout.unit < 50;

  return (
    <div className={`hud-in ${compact ? 'is-compact' : ''}`}>
      <div className={`clock-sign ${c?.paused ? 'is-paused' : ''} ${left < 60_000 && !c?.paused ? 'is-low' : ''}`} aria-label="Tempo restante">
        {c?.paused ? 'PAUSA' : left > 0 ? clock(left) : 'FIM'}
        {g.finalRound && <small>Última rodada</small>}
      </div>

      <div className="dice" aria-live="polite" ref={diceBox}>
        {(rolling || !roll ? [5, 2] : roll.dice).map((v, i) => (
          <Die key={i} value={v} rolling={rolling} spin={spin + i} />
        ))}
      </div>

      {g.phase === 'finished' ? (
        <p className="hud-status">Fim de jogo</p>
      ) : myRoll && me ? (
        <div className="hud-actions">
          <button
            className="pill pill-blue pill-big"
            onClick={() => {
              setPressed(true);
              act({ type: 'ROLL' });
            }}
          >
            {me.inJail ? (me.evader ? 'Cumprir pena' : 'Tentar dupla') : 'Jogar dados'}
            {secs !== null && <span className="pill-secs">{secs}</span>}
          </button>
          {me.inJail && (
            <div className="hud-row">
              <button className="pill pill-yellow" onClick={() => act({ type: 'PAY_JAIL' })} disabled={me.cash < jailFine(me)}>
                Pagar {money(jailFine(me))}
              </button>
              {(me.held.includes('advogado') || (me.shop ?? []).includes('advogado')) && (
                <button className="pill pill-green" onClick={() => act({ type: 'USE_JAIL_CARD' })}>
                  Usar Habeas Corpus
                </button>
              )}
            </div>
          )}
          <button className="pill pill-gray pill-small" onClick={onShop}>
            🛒 Lojinha
          </button>
          {me.inJail && (
            <p className="hud-hint">
              {me.evader ? (me.jailAttempts >= 3 ? 'Pena cumprida: jogue para sair' : `Pena por sonegação: faltam ${3 - me.jailAttempts} rodadas (dupla não solta)`) : `Tentativa ${me.jailAttempts + 1} de 3 na ${ctx.square(8)}`}
            </p>
          )}
        </div>
      ) : mine && d.kind === 'prompt' && (d.prompt.kind === 'tour' || d.prompt.kind === 'championship') ? (
        <PickBar d={d} secs={secs} />
      ) : (
        <p className="hud-status">
          {rolling ? 'Rolando os dados…' : who && who !== you ? `Vez de ${name(who)}` : who === you ? 'Sua vez' : ''}
          {secs !== null && who !== you && d.kind !== 'turn' && <span className="hud-secs">{secs}s</span>}
        </p>
      )}

      {news && (
        <p key={news.id} className={`hud-news tone-${news.tone}`}>
          {news.text}
        </p>
      )}

      {cd && g.phase !== 'finished' && (
        <div className={`timer-bar ${mine ? 'is-mine' : ''} ${cd.left < 5000 ? 'is-low' : ''}`} aria-hidden="true">
          <i style={{ width: `${(cd.left / cd.total) * 100}%` }} />
        </div>
      )}
    </div>
  );
}

/** Volta ao Mundo e Campeonato: a escolha é feita tocando no tabuleiro. */
function PickBar({ d, secs }: { d: Extract<Decision, { kind: 'prompt' }>; secs: number | null }) {
  const { act, money } = useRoomCtx();
  const p = d.prompt;
  if (p.kind === 'tour') {
    return (
      <div className="hud-actions">
        <p className="hud-ask">Toque na casa para onde quer voar ({money(50)})</p>
        <button className="pill pill-gray" onClick={() => act({ type: 'SKIP_TOUR' })}>
          Ficar aqui {secs !== null && <span className="pill-secs">{secs}</span>}
        </button>
      </div>
    );
  }
  return (
    <div className="hud-actions">
      <p className="hud-ask">Toque numa cidade sua para sediar a Copa</p>
      {secs !== null && <span className="hud-secs">{secs}s</span>}
    </div>
  );
}
